import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiMail, FiPhone, FiFileText } = FiIcons;

const PrivacyPolicy = () => {
  const lastUpdated = "28/04/2025";

  return (
    <div className="pt-16 min-h-screen bg-warm-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-warm-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center mb-6"
            >
              <div className="bg-primary-600 w-20 h-20 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiShield} className="h-10 w-10 text-white" />
              </div>
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-warm-900 mb-6"
            >
              Privacy Policy
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-warm-700 max-w-3xl mx-auto"
            >
              Informazioni sulla privacy e protezione dei dati personali
            </motion.p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="mb-8 text-sm text-warm-600">
              Ultimo aggiornamento: {lastUpdated}
            </div>

            <div className="prose prose-warm max-w-none">
              <p className="text-lg mb-8">
                Ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003 così come modificato dal D.Lgs. 101/2018, 
                la presente Privacy Policy descrive come Elvira Vigliano Property Manager ("noi", "nostro", "ci") raccoglie, 
                utilizza e protegge le informazioni personali degli utenti che visitano il nostro sito web o utilizzano i nostri servizi.
              </p>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">1. Titolare del Trattamento</h2>
              <p className="mb-4">
                Il titolare del trattamento dei dati è Elvira Vigliano Property Manager con sede in Italia. 
                Per qualsiasi domanda riguardo questa Privacy Policy o per esercitare i tuoi diritti, puoi contattarci ai seguenti recapiti:
              </p>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><strong>Email:</strong> elviraviglianopm@gmail.com</li>
                <li><strong>Telefono:</strong> +39 377 045 7022</li>
              </ul>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">2. Dati Raccolti</h2>
              <p className="mb-4">Raccogliamo i seguenti dati personali:</p>
              <h3 className="text-xl font-semibold text-warm-800 mt-6 mb-3">Dati forniti volontariamente</h3>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li>Nome e cognome</li>
                <li>Indirizzo email</li>
                <li>Numero di telefono</li>
                <li>Indirizzo dell'immobile</li>
                <li>Informazioni sull'immobile (metratura, numero di stanze, servizi)</li>
                <li>Informazioni relative alla richiesta di servizi</li>
              </ul>

              <h3 className="text-xl font-semibold text-warm-800 mt-6 mb-3">Dati raccolti automaticamente</h3>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li>Indirizzo IP</li>
                <li>Tipo di browser e dispositivo</li>
                <li>Pagine visitate sul nostro sito</li>
                <li>Durata della visita</li>
                <li>Sistema operativo</li>
              </ul>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">3. Finalità del Trattamento</h2>
              <p className="mb-4">I dati personali sono trattati esclusivamente per le seguenti finalità:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><strong>Gestione delle richieste di informazioni:</strong> per rispondere alle tue richieste e fornirti informazioni sui nostri servizi</li>
                <li><strong>Esecuzione del contratto:</strong> per erogare i servizi richiesti e gestire la relazione contrattuale</li>
                <li><strong>Adempimenti fiscali e contabili:</strong> per adempiere agli obblighi di legge</li>
                <li><strong>Marketing interno:</strong> per inviarti informazioni su servizi simili a quelli richiesti (senza profilazione)</li>
                <li><strong>Miglioramento del servizio:</strong> per analizzare l'utilizzo del sito e migliorare l'esperienza dell'utente</li>
              </ul>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">4. Base Giuridica del Trattamento</h2>
              <p className="mb-4">Il trattamento dei dati è basato su:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><strong>Art. 6(1)(b) GDPR:</strong> esecuzione di un contratto di cui l'interessato è parte</li>
                <li><strong>Art. 6(1)(f) GDPR:</strong> nostro interesse legittimo a gestire la tua richiesta e fornire i nostri servizi</li>
                <li><strong>Obblighi di legge:</strong> adempimento di obblighi fiscali e contabili</li>
                <li><strong>Consenso:</strong> per le attività di marketing interno (se fornito)</li>
              </ul>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">5. Comunicazione dei Dati</h2>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <p className="text-red-700">
                  <strong>Attenzione:</strong> I tuoi dati personali non vengono venduti né comunicati a terzi per scopi commerciali. 
                  I dati sono utilizzati esclusivamente internamente da Elvira Vigliano Property Manager per le finalità descritte.
                </p>
              </div>
              <p className="mb-4">Potremmo comunicare i tuoi dati a:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><strong>Professionisti e consulenti:</strong> commercialisti, legali, per adempimenti professionali</li>
                <li><strong>Istituti di pagamento:</strong> per la gestione dei pagamenti</li>
                <li><strong>Autorità pubbliche:</strong> quando obbligato per legge</li>
              </ul>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">6. Trasferimento Dati fuori dall'UE</h2>
              <p className="mb-6">
                I tuoi dati personali sono trattati esclusivamente all'interno dell'Unione Europea e non vengono trasferiti in paesi terzi.
              </p>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">7. Periodo di Conservazione</h2>
              <p className="mb-4">I dati personali sono conservati per i seguenti periodi:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><strong>Dati contrattuali:</strong> per tutta la durata del rapporto contrattuale e per 10 anni dopo la sua conclusione</li>
                <li><strong>Dati fiscali:</strong> per 10 anni dall'ultima registrazione contabile</li>
                <li><strong>Dati di navigazione:</strong> per 24 mesi</li>
                <li><strong>Marketing:</strong> fino al ritiro del consenso</li>
              </ul>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">8. Diritti dell'Interessato</h2>
              <p className="mb-4">Ai sensi del GDPR, hai il diritto di:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><strong>Diritto di accesso:</strong> ottenere conferma dell'esistenza di dati che ti riguardano</li>
                <li><strong>Diritto di rettifica:</strong> ottenere la rettifica dei dati inesatti</li>
                <li><strong>Diritto di cancellazione:</strong> ottenere la cancellazione dei dati (nei limiti di legge)</li>
                <li><strong>Diritto di opposizione:</strong> opporsi al trattamento dei dati per marketing diretto</li>
                <li><strong>Diritto alla portabilità:</strong> ricevere i dati in un formato strutturato di uso comune</li>
                <li><strong>Diritto di limitazione:</strong> ottenere la limitazione del trattamento in specifiche circostanze</li>
              </ul>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">9. Sicurezza dei Dati</h2>
              <p className="mb-6">
                Implementiamo misure di sicurezza tecniche e organizzative adeguate per proteggere i tuoi dati personali 
                da perdita, uso improprio, accesso non autorizzato, divulgazione, alterazione o distruzione.
              </p>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">10. Modifiche alla Privacy Policy</h2>
              <p className="mb-6">
                Ci riserviamo il diritto di modificare questa Privacy Policy in qualsiasi momento. 
                Le modifiche saranno pubblicate su questa pagina con la data di aggiornamento.
              </p>

              <h2 className="text-2xl font-bold text-warm-900 mt-12 mb-6">11. Come Esercitare i Tuoi Diritti</h2>
              <p className="mb-4">
                Per esercitare i tuoi diritti o per qualsiasi domanda riguardo questa Privacy Policy, 
                puoi contattarci utilizzando i seguenti recapiti:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-warm-50 p-6 rounded-lg">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiMail} className="h-5 w-5 text-primary-600 mr-2" />
                    <h3 className="font-semibold text-warm-900">Email</h3>
                  </div>
                  <p className="text-warm-700">elviraviglianopm@gmail.com</p>
                </div>
                <div className="bg-warm-50 p-6 rounded-lg">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiPhone} className="h-5 w-5 text-primary-600 mr-2" />
                    <h3 className="font-semibold text-warm-900">Telefono</h3>
                  </div>
                  <p className="text-warm-700">+39 377 045 7022</p>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-warm-200">
              <Link 
                to="/" 
                className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
              >
                <SafeIcon icon={FiFileText} className="h-4 w-4 mr-2" />
                Torna alla Home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;